import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Database, Target, Lock, UserCheck, Mail } from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";

const Section = ({ icon: Icon, n, title, testid, children }) => (
  <section data-testid={testid} className="border-t border-neutral-200 py-10 first:border-t-0 first:pt-0">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85]">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
        {n}. {title}
      </h2>
    </div>
    <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-neutral-700 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_strong]:text-neutral-900 [&_a]:font-semibold [&_a]:text-[#0A8B85] [&_a]:underline-offset-4 hover:[&_a]:underline">
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
          <Link to="/" data-testid="privacy-policy-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#7BD3CE]">
            Legal · Unlock Your Capital Potential
          </p>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">Privacy Policy</h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            How Clinq collects, uses and protects your personal and business data, in line
            with the Kenya Data Protection Act, 2019.
          </p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-white/40">
            Last updated: June 2026
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14 lg:py-16">
        <Section icon={ShieldCheck} n={1} title="Role of Clinq" testid="pp-role">
          <p>
            Clinq acts as a <strong>Data Controller</strong> within the meaning of the Kenya
            Data Protection Act, 2019, in respect of the personal and business information
            provided by founders and funders on our platform. This means Clinq determines the
            purposes and the means of processing that data — including how it is used to
            calculate Capital and Project Readiness Scores, generate readiness reports, and
            optimise investment returns for funders through mandate-based matching.
          </p>
          <p>
            Clinq is committed to processing all personal data lawfully, fairly and
            transparently, and in accordance with the principles set out by the Office of the
            Data Protection Commissioner (ODPC) of Kenya.
          </p>
        </Section>

        <Section icon={Database} n={2} title="Data We Collect" testid="pp-collection">
          <p>Depending on how you use Clinq, we collect the following categories of data:</p>
          <ul>
            <li><strong>Identity information</strong> — your name, email address, phone number, business role and login credentials.</li>
            <li><strong>Business information</strong> — company name, registration details, sector, headquarters, revenue stage and team details.</li>
            <li><strong>Business financials</strong> — financial models, management accounts, audited accounts, bank statements and related figures extracted from documents you upload.</li>
            <li><strong>Pitch decks and supporting documents</strong> — legal documents, tax compliance certificates, cap tables and other materials you choose to share.</li>
            <li><strong>Investor risk profiles</strong> — for funders: preferred sectors, ticket sizes, investment stages, instruments and geographic focus.</li>
            <li><strong>Usage data</strong> — page visits, deal views, document access events and audit-trail records generated while using the platform.</li>
          </ul>
        </Section>

        <Section icon={Target} n={3} title="Purpose of Processing" testid="pp-purpose">
          <p>We process your data for the following purposes:</p>
          <ul>
            <li>To provide <strong>business health insights</strong> — analysing your documents to show you exactly where your business stands and what to improve.</li>
            <li>To <strong>generate readiness reports</strong> — including your Capital or Project Readiness Score, improvement roadmap and borrowing-capacity estimates.</li>
            <li>To <strong>facilitate matching between founders and funders</strong> — surfacing businesses and projects to funders whose mandates and risk profiles align, and vice versa.</li>
            <li>To operate the platform — account management, transactional emails, payment processing, support and security monitoring.</li>
            <li>To meet our legal, regulatory and audit obligations.</li>
          </ul>
          <p>
            Sensitive business documents are never shown to a funder without your explicit
            in-platform approval, and all access is audit-logged.
          </p>
        </Section>

        <Section icon={Lock} n={4} title="Data Security" testid="pp-security">
          <p>
            We implement <strong>robust technical and organisational measures</strong> to
            protect your data, including encryption in transit, access controls, NDA-gated
            data rooms, audit logging and bank-grade security practices across our
            infrastructure.
          </p>
          <p>
            <strong>We do not sell your personal information to third parties.</strong> Data
            is shared only with your consent (for example, when you approve a funder's
            request for access), with service providers who help us operate the platform
            under strict confidentiality obligations, or where required by law.
          </p>
        </Section>

        <Section icon={UserCheck} n={5} title="Your Rights" testid="pp-rights">
          <p>
            In line with the Kenya Data Protection Act and ODPC guidelines, you have the
            right to:
          </p>
          <ul>
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Rectify</strong> — ask us to correct inaccurate or incomplete data.</li>
            <li><strong>Delete</strong> — request erasure of your personal data, subject to any legal retention obligations.</li>
            <li><strong>Object or restrict</strong> — object to certain processing or ask us to limit how your data is used.</li>
            <li><strong>Withdraw consent</strong> — where processing is based on consent, withdraw it at any time.</li>
          </ul>
          <p>
            To exercise any of these rights, contact our Data Protection Officer using the
            details below. You also have the right to lodge a complaint with the Office of
            the Data Protection Commissioner (ODPC) of Kenya.
          </p>
        </Section>

        <Section icon={Mail} n={6} title="Contact — Data Protection Officer" testid="pp-contact">
          <p>
            For any questions about this policy, your data, or to exercise your rights,
            contact our Data Protection Officer:
          </p>
          <div className="rounded-2xl border border-neutral-200 bg-[#F4F8F8] p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-neutral-500">Data Protection Officer</p>
            <a href="mailto:dpo@clinq.africa" data-testid="pp-dpo-email"
              className="mt-2 inline-block text-lg font-bold text-[#0A8B85]">
              dpo@clinq.africa
            </a>
            <p className="mt-2 text-sm text-neutral-600">
              Clinq · SNDBX International, CMS House, 850 Chania Ave, Hurlingham, Nairobi, Kenya
            </p>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
